import type {
  ReploAnnouncement,
  ReploAnnouncementTag,
} from "schemas/generated/announcement";

import * as React from "react";
import { useEffect, useState } from "react";

import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { useListAnnouncements } from "@v2/features/announcements/hooks/useListAnnouncements";
import { extractTextFromFirstParagraph } from "@v2/features/announcements/utils/announcement";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";

const AnnouncementMessage: React.FC<{
  tags?: ReploAnnouncementTag[];
}> = ({ tags }) => {
  const [announcement, setAnnouncement] = useState<ReploAnnouncement | null>(
    null,
  );
  const [lastSeenAnnouncementId, setLastSeenAnnouncementId] =
    useLocalStorageState<string>(
      "replo.announcements.message.lastSeenAnnouncementId",
      "",
      {
        validate: (value): value is string => typeof value === "string",
      },
    );
  const logEvent = useLogAnalytics();

  const { announcements, isLoading } = useListAnnouncements({
    tags: tags,
    limit: 1,
  });

  useEffect(() => {
    if (
      announcements.length > 0 &&
      !isLoading &&
      announcements[0] &&
      announcements[0].id !== lastSeenAnnouncementId
    ) {
      setAnnouncement(announcements[0]);
    }
  }, [lastSeenAnnouncementId, isLoading, announcements]);

  const handleAnnouncementClick = () => {
    if (announcement && announcement.ctaLink) {
      window.open(announcement.ctaLink, "_blank");
      logEvent("announcements.clicked", {
        announcementId: announcement.id,
        announcementWebflowId: announcement.webflowId,
        source: "AnnouncementMessage",
      });
    }
  };

  const handleCloseAnnouncement = () => {
    setAnnouncement(null);
    if (announcement) {
      setLastSeenAnnouncementId(announcement.id);
      logEvent("announcements.dismissed", {
        announcementId: announcement.id,
        announcementWebflowId: announcement.webflowId,
        source: "AnnouncementMessage",
      });
    }
  };

  if (isLoading || !announcement) {
    return null;
  }

  return (
    <Alert
      variant="beta"
      onClose={handleCloseAnnouncement}
      description={extractTextFromFirstParagraph(announcement.description)}
      action={
        announcement.ctaText && announcement.ctaLink
          ? { text: announcement.ctaText, onClick: handleAnnouncementClick }
          : undefined
      }
    />
  );
};

export default AnnouncementMessage;
